// Admin Events Service
// Owner view of beta events under the size gate (ITER-099). Tier and gate changes go through callables only.

import { collection, onSnapshot, query, where, Unsubscribe } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { docToEvent, Event } from './events';

export interface SizeGateUpdate {
  gateEnabled?: boolean;
  tierCap?: number;
}

// Mirrors DeletedEventRow in functions/src/utils/eventDeletion.ts (ITER-154)
export interface DeletedEventRow {
  eventId: string;
  name: string;
  date: string | null;
  djUid: string | null;
  djName: string | null;
  djEmail: string | null;
  djAccountExists: boolean;
  deletedAt: string | null;
  requestCount: number;
}

export interface SizeGateResult {
  gateEnabled: boolean;
  tierCap: number;
  pendingTierCap: number | null;
}

export function subscribeToGatedEvents(
  onChange: (events: Event[]) => void,
  onError: (error: Error) => void
): Unsubscribe {
  const gatedQuery = query(collection(db, 'events'), where('sizeGate.applies', '==', true));
  return onSnapshot(
    gatedQuery,
    (snapshot) => onChange(snapshot.docs.filter((d) => d.data().deletedAt == null).map(docToEvent)),
    onError
  );
}

export async function approveTierUpgrade(eventId: string): Promise<{ tierCap: number }> {
  const callable = httpsCallable<{ eventId: string }, { tierCap: number }>(functions, 'approveTierUpgrade');
  const result = await callable({ eventId });
  return result.data;
}

export async function updateEventSizeGate(eventId: string, update: SizeGateUpdate): Promise<SizeGateResult> {
  const callable = httpsCallable<{ eventId: string } & SizeGateUpdate, SizeGateResult>(functions, 'updateEventSizeGate');
  const result = await callable({ eventId, ...update });
  return result.data;
}

export async function listDeletedEvents(): Promise<DeletedEventRow[]> {
  const callable = httpsCallable<Record<string, never>, { events: DeletedEventRow[] }>(functions, 'listDeletedEvents');
  const result = await callable({});
  return result.data.events;
}

export async function restoreDeletedEvent(eventId: string): Promise<void> {
  const callable = httpsCallable<{ eventId: string }, { ok: boolean }>(functions, 'restoreDeletedEvent');
  await callable({ eventId });
}
